import { ReactNode, useMemo } from 'react'
import { Column, ColumnInstance, Row, useTable } from 'react-table'
import clsx from 'clsx'
import { KTCardBody } from '../../../../../_metronic/helpers'
import { KTablePagination } from './KTablePagination'
import { Button, ButtonWrapper } from '../Button'
import { useKhipu } from '../../../hooks/useKhipu'
import { useHoteleria } from '../../../hooks/useHoteleria'
import { useKTable } from '../../../contexts/KTableProvider'

interface RowProps<T extends object> {
  row: Row<T>
  getRowClassName?: (row: Row<T>) => string // 👈 NUEVO
}

interface ColumnProps<T extends object> {
  column: ColumnInstance<T>
}

const CustomRowClient = <T extends object>({ row, getRowClassName }: RowProps<T>) => {
  const groupConfig = (row.original as any).groupConfig

  const rowClass = getRowClassName ? getRowClassName(row) : ''

  if (groupConfig) {
    const { startIndex, count } = groupConfig
    return (
      <tr {...row.getRowProps()} className={rowClass}>
        {row.cells.map((cell, i) => {
          // Si estamos en la columna donde empieza el agrupado
          if (i === startIndex) {
            return (
              <td
                {...cell.getCellProps()}
                colSpan={count}
                className={clsx('text-dark fw-semibold text-left', (cell.column as any).className)}
                key={cell.column.id}
                style={{
                  width: (cell.column as any).width,
                  minWidth: (cell.column as any).minWidth,
                  maxWidth: (cell.column as any).maxWidth,
                }}
              >
                {cell.render('Cell')}
              </td>
            )
          }
          // Omitir las otras columnas del rango agrupado
          if (i > startIndex && i < startIndex + count) {
            return null
          }
          // Renderizar normal las demás
          return (
            <td
              {...cell.getCellProps()}
              className={clsx('text-dark fw-semibold text-left', (cell.column as any).className)}
              key={cell.column.id}
              style={{
                width: (cell.column as any).width,
                minWidth: (cell.column as any).minWidth,
                maxWidth: (cell.column as any).maxWidth,
              }}
            >
              {cell.render('Cell')}
            </td>
          )
        })}
      </tr>
    )
  }

  // Render normal para filas no resumen
  return (
    <tr {...row.getRowProps()} className={rowClass}>
      {row.cells.map((cell) => (
        <td
          {...cell.getCellProps()}
          className={clsx('text-dark fw-semibold text-left', (cell.column as any).className)}
          style={{
            width: (cell.column as any).width,
            minWidth: (cell.column as any).minWidth,
            maxWidth: (cell.column as any).maxWidth,
          }}
        >
          {cell.render('Cell')}
        </td>
      ))}
    </tr>
  )
}

const CustomHeaderColumn = <T extends object>({ column }: ColumnProps<T>) => {
  return (
    <>
      {column.Header && typeof column.Header === 'string' ? (
        <th
          {...column.getHeaderProps()}
          className={clsx((column as any).headerClassName || (column as any).className)}
          style={{
            width: (column as any).width,
            minWidth: (column as any).minWidth,
            maxWidth: (column as any).maxWidth,
          }}
        >
          {column.render('Header')}
        </th>
      ) : (
        <th
          {...column.getHeaderProps()}
          className={clsx((column as any).headerClassName || (column as any).className)}
          style={{
            width: (column as any).width,
            minWidth: (column as any).minWidth,
            maxWidth: (column as any).maxWidth,
          }}
        >
          {column.render('Header')}
        </th>
      )}
    </>
  )
}

type Size = 'small' | 'medium' | 'large'
interface TableProps<T extends object> {
  data: T[]
  size?: Size
  columns: ReadonlyArray<Column<T>>
  classNameProps?: string
  showPagination?: boolean
  clearFilters?: boolean
  showButtons?: boolean
  fromDetail?: boolean
  paddingTable?: boolean
  showButtonAdd?: boolean
  labelButtonAdd?: string
  handleButtonAdd?: () => void
  getRowClassName?: (row: Row<T>) => string
  showPageSizeSelector?: boolean
  paginationLeftActions?: ReactNode
  paginationRightActions?: ReactNode
  paginationArrowsNextToPageSize?: boolean
  enableFixedLayout?: boolean
}
const KTable = <T extends object>({
  data,
  size = 'large',
  columns,
  classNameProps,
  showPagination = true,
  clearFilters = false,
  showButtons = true,
  fromDetail = false,
  paddingTable = true,
  showButtonAdd = false,
  labelButtonAdd,
  handleButtonAdd,
  getRowClassName,
  showPageSizeSelector = false,
  paginationLeftActions,
  paginationRightActions,
  paginationArrowsNextToPageSize = false,
  enableFixedLayout = false,
}: TableProps<T>) => {
  const mData = useMemo(() => data, [data])
  const mColumns = useMemo(() => columns, [columns])
  const { searchValues, changeSearchValues, searchValuesDetail, changeSearchValuesDetail } =
    useKhipu()
  const { currentTabItem, changeCurrentTabItem } = useHoteleria()
  const { getTableProps, getTableBodyProps, headers, rows, prepareRow } = useTable({
    columns: mColumns,
    data: mData,
  })
  const {
    setUpdateTable,
    setClearTable,
    setUpdateTableDetail,
    setClearTableDetail,
    updateTable,
    updateTableDetail,
    clearTable,
    clearTableDetail,
  } = useKTable()

  const handleSearch = () => {
    changeCurrentTabItem({ ...currentTabItem, loadingTable: true })
    setUpdateTable(true)
  }
  const handleClear = () => {
    changeCurrentTabItem({ ...currentTabItem, loadingTable: true })
    setUpdateTable(true)
    setClearTable(true)
    changeSearchValues({})
  }

  const handleSearchDetail = () => {
    changeCurrentTabItem({ ...currentTabItem, loadingTable: true })
    setUpdateTableDetail(true)
  }
  const handleClearDetail = () => {
    changeCurrentTabItem({ ...currentTabItem, loadingTable: true })
    setUpdateTableDetail(true)
    setClearTableDetail(true)
    changeSearchValuesDetail({})
  }
  const classMinHeight =
    mData.length === 0 ? 'min-height-350' : mData.length <= 2 ? 'min-height-250' : ''
  return (
    <>
      <KTCardBody className={paddingTable ? 'py-2' : size === 'small' ? 'p-1' : 'p-0'}>
        {showPagination && (
          <KTablePagination
            showPageSizeSelector={showPageSizeSelector}
            leftActions={paginationLeftActions}
            rightActions={paginationRightActions}
            arrowsNextToPageSize={paginationArrowsNextToPageSize}
          />
        )}
        {fromDetail ? (
          <>
            {Object.keys(searchValuesDetail).length > 0 ? (
              <ButtonWrapper>
                <Button label='Buscar' onClick={handleSearchDetail} />
                <Button label='Limpiar' onClick={handleClearDetail} />
              </ButtonWrapper>
            ) : (
              <></>
            )}
          </>
        ) : (
          <>
            {showButtons &&
              (showButtonAdd || Object.keys(searchValues).length > 0 || clearFilters) && (
                <ButtonWrapper>
                  {showButtonAdd && <Button label={labelButtonAdd} onClick={handleButtonAdd} />}
                  {Object.keys(searchValues).length > 0 && (
                    <Button label='Buscar' onClick={handleSearch} />
                  )}
                  {(Object.keys(searchValues).length > 0 || clearFilters) && (
                    <Button label='Limpiar' onClick={handleClear} />
                  )}
                </ButtonWrapper>
              )}
          </>
        )}

        <div
          className='table-wrapper'
          style={{
            maxWidth: '100%',
            overflowX: 'auto',
            height: '100%',
            borderRadius: '8px',
          }}
        >
          <table
            id='kt_table_users'
            className={clsx(
              'table align-middle table-row-dashed fs-6 dataTable no-footer',
              classMinHeight,
              classNameProps,
              { 'k-table': enableFixedLayout },
            )}
            style={{
              ...(enableFixedLayout ? { tableLayout: 'fixed', width: '100%' } : {}),
            }}
            {...getTableProps()}
          >
            <thead>
              <tr className='text-start text-dark fw-bolder fs-7 bg-light'>
                {headers.map((column: ColumnInstance<T>) => (
                  <CustomHeaderColumn key={column.id} column={column} />
                ))}
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold' {...getTableBodyProps()}>
              {rows.length > 0 ? (
                rows.map((row: Row<T>, i) => {
                  prepareRow(row)
                  return (
                    <CustomRowClient
                      row={row}
                      key={`row-${i}-${row.id}`}
                      getRowClassName={getRowClassName}
                    />
                  )
                })
              ) : (
                <tr>
                  <td colSpan={columns.length}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      No hay información
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </KTCardBody>
    </>
  )
}

export { KTable }
